import { queryOptions, useQuery } from '@tanstack/react-query';

import { publicApi } from '@/lib/api-clients/rest-client';
import { getMarketStore } from '@/store/use-markets-store';
import { parseDecimalToBigInt } from '@/utils/value-format';

type OIsResponse = {
  ois: OIRecord[];
};

type OIRecord = {
  symbol: string;
  shortOi: string;
  longOi: string;
};

async function getOpenInterest(symbols?: string[]): Promise<OIRecord[]> {
  return publicApi
    .get('/oi', {
      params: symbols ? { symbols: symbols.join(',') || undefined } : {},
    })
    .then((res: OIsResponse) => {
      res.ois.forEach((ticker: OIRecord) => {
        const {
          setMarketData,
          marketData: { markPrice },
          marketSpec,
        } = getMarketStore(ticker.symbol).getState();
        const longCoinsSize = parseDecimalToBigInt(
          ticker.longOi,
          marketSpec.sizeDecimals,
        );
        const shortCoinsSize = parseDecimalToBigInt(
          ticker.shortOi,
          marketSpec.sizeDecimals,
        );
        setMarketData({
          openInterest: (longCoinsSize + shortCoinsSize) * markPrice,
        });
      });
      return res.ois;
    });
}

const getOpenInterestQueryOptions = ({
  symbols,
  refetchInterval,
}: { symbols?: string[]; refetchInterval?: number | false } = {}) => {
  return queryOptions({
    queryKey: symbols ? ['open-interest', { symbols }] : ['open-interest'],
    queryFn: () => getOpenInterest(symbols),
    refetchInterval: refetchInterval ?? 10_000,
  });
};

type UseOpenInterestOptions = {
  symbols?: string[];
  disabled?: boolean;
  refetchInterval?: number | false;
};

export default function useOpenInterest({
  symbols,
  disabled,
  refetchInterval,
}: UseOpenInterestOptions) {
  return useQuery({
    ...getOpenInterestQueryOptions({ symbols, refetchInterval }),
    enabled: !disabled,
  });
}
